<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <button @click="handleBack"
          class="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
          <ArrowLeftIcon class="w-6 h-6" />
        </button>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">公告详情</h1>
      </div>
      <div v-if="announcement" class="flex gap-2">
        <button @click="handleEdit"
          class="primary-btn text-sm font-medium">
          编辑
        </button>
        <button @click="handleDelete"
          class="danger-btn text-sm font-medium">
          删除
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 公告详情 -->
    <div v-else-if="announcement" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <!-- 标题和标签 -->
      <div class="mb-6">
        <div class="flex items-center gap-3 mb-3">
          <!-- 优先级标签 -->
          <span :class="getPriorityBadgeClass(announcement.priority)" class="px-2 py-1 text-xs font-medium rounded">
            {{ AnnouncementPriorityLabel[announcement.priority] }}
          </span>
          <!-- 弹窗标签 -->
          <span v-if="announcement.isPopup"
            class="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
            🔔 弹窗公告
          </span>
          <span v-else
            class="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            📋 普通公告
          </span>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ announcement.title }}</h2>
      </div>

      <!-- 元信息 -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
        <div>
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">创建时间:</span>
          <span class="ml-2 text-sm text-gray-900 dark:text-gray-100">{{ formatDate(announcement.createdAt) }}</span>
        </div>
        <div>
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">更新时间:</span>
          <span class="ml-2 text-sm text-gray-900 dark:text-gray-100">{{ formatDate(announcement.updatedAt) }}</span>
        </div>
        <div v-if="announcement.validFrom">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">生效时间:</span>
          <span class="ml-2 text-sm text-gray-900 dark:text-gray-100">{{ formatDate(announcement.validFrom) }}</span>
        </div>
        <div v-if="announcement.validUntil">
          <span class="text-sm font-medium text-gray-500 dark:text-gray-400">失效时间:</span>
          <span class="ml-2 text-sm text-gray-900 dark:text-gray-100">{{ formatDate(announcement.validUntil) }}</span>
        </div>
      </div>

      <!-- 内容 -->
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">公告内容</h3>
        <div class="prose dark:prose-invert max-w-none p-4 bg-gray-50 dark:bg-gray-700 rounded-md"
          v-html="announcement.content"></div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">加载失败</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">无法加载公告数据</p>
      <button @click="handleBack"
        class="primary-btn">
        返回列表
      </button>
    </div>

    <!-- 删除确认模态框 -->
    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import { getAnnouncementAPI, deleteAnnouncementAPI } from '@/api/announcement'
import { AnnouncementPriorityLabel, AnnouncementPriority, type Announcement } from '@/types/announcement'
import { formatDate } from '@/utils/date'
import toaster from '@/utils/toaster'
import ConfirmModal from '@/components/ui/ConfirmModal.vue'

/**
 * 路由实例
 */
const router = useRouter()
const route = useRoute()

/**
 * 状态
 */
const loading = ref(true)
const announcement = ref<Announcement | null>(null)
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null)

/**
 * 加载公告数据
 */
const loadAnnouncement = async () => {
  const announcementId = Number(route.params.id)
  if (!announcementId) {
    toaster.error('无效的公告 ID')
    router.push('/announcement')
    return
  }

  loading.value = true
  try {
    const response = await getAnnouncementAPI(announcementId)
    if (response.data) {
      announcement.value = response.data
    } else {
      toaster.error('加载公告失败')
      router.push('/announcement')
    }
  } catch (error) {
    console.error('加载公告失败:', error)
    toaster.error('加载公告失败')
    router.push('/announcement')
  } finally {
    loading.value = false
  }
}

/**
 * 获取优先级徽章样式类
 */
const getPriorityBadgeClass = (priority: AnnouncementPriority): string => {
  const classMap = {
    [AnnouncementPriority.HIGH]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [AnnouncementPriority.NORMAL]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [AnnouncementPriority.LOW]: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  }
  return classMap[priority] || classMap[AnnouncementPriority.NORMAL]
}

/**
 * 返回列表页
 */
const handleBack = () => {
  router.push('/announcement')
}

/**
 * 处理编辑
 */
const handleEdit = () => {
  router.push(`/announcement/${route.params.id}/edit`)
}

/**
 * 处理删除
 */
const handleDelete = async () => {
  if (!announcement.value || !confirmModalRef.value) return

  try {
    const confirmed = await confirmModalRef.value.confirm(
      `确定要删除公告"${announcement.value.title}"吗？<br><br><span style="color: #ef4444;">此操作不可恢复</span>`,
      '确认删除'
    )

    if (!confirmed) return

    await deleteAnnouncementAPI(announcement.value.id)
    toaster.success('删除成功')
    router.push('/announcement')
  } catch (error) {
    toaster.error('删除失败')
    console.error('删除公告失败:', error)
  }
}

// 组件挂载时加载数据
onMounted(() => {
  loadAnnouncement()
})
</script>
